import { useParams } from 'react-router-dom';
import queryString from 'query-string';
import { t } from 'ttag';

import { UserReviewsFeedContainer } from '@containers/UserReviewsFeed';
import { useSearchParams } from '@utils/common';
import {
  ReviewCategory,
  ReviewRating,
} from '@models';
import { TabBar } from '@ui/TabBar';
import { Tab } from '@ui/Tab';
import { ProfileReviewsFilter } from '@components/ProfileReviewsFilter';

import { useUserReviewsCountersQuery } from './graphql.v3.generated';

import s from './ProfileReviewsPage.sss';

interface RatingParams {
  p: ReviewRating,
  n: ReviewRating,
  [key: string]: ReviewRating,
}

interface CategoryParams {
  s: ReviewCategory,
  b: ReviewCategory,
  [key: string]: ReviewCategory,
}

const ratingParamToReviewRating: RatingParams = {
  p: ReviewRating.Positive,
  n: ReviewRating.Negative,
};

const categoryParamToReviewCategory: CategoryParams = {
  s: ReviewCategory.Buyer,
  b: ReviewCategory.Seller,
};

type SearchParams = {
  r?: keyof typeof ratingParamToReviewRating // Rating?: Positive | Negative
  t?: keyof typeof categoryParamToReviewCategory // Category?: Seller
};

type Params = {
  username?: string
};

type ReviewGroup = 'seller' | 'buyer' | 'positive' | 'negative';

export const ProfileReviewsPage: React.FC = () => {
  const [params] = useSearchParams<SearchParams>();
  const { username } = useParams<Params>();

  if (!username) {
    throw new Error('No username in params');
  }

  const category = categoryParamToReviewCategory[
    params.t as keyof typeof categoryParamToReviewCategory
  ] || ReviewCategory.Buyer;

  const { data } = useUserReviewsCountersQuery({
    variables: { username, category },
    fetchPolicy: 'no-cache',
    skip: !username || !params.t,
  });

  return (
    (
      <div className={s.root}>
        <ProfileReviewsFilter
          className={s.reviewsFilter}
          username={username}
          sellerCount={data?.seller?.reviews?.pageInfo?.total}
          buyerCount={data?.buyer?.reviews?.pageInfo?.total}
        />
        <div className={s.feedSide}>
          <TabBar>
            <Tab
              to={{ search: queryString.stringify({ ...params, r: undefined }) }}
              isActive={() => !params.r}
            >
              {t`Усi`}
            &nbsp;
              {data?.[category?.toLowerCase() as ReviewGroup]?.reviews?.pageInfo?.total}
            </Tab>
            <Tab
              to={{ search: queryString.stringify({ ...params, r: 'p' }) }}
              isActive={() => params.r === 'p'}
            >
              {t`Позитивні`}
            &nbsp;
              {data?.positive?.reviews?.pageInfo?.total}
            </Tab>
            <Tab
              to={{ search: queryString.stringify({ ...params, r: 'n' }) }}
              isActive={() => params.r === 'n'}
            >
              {t`Негативні`}
            &nbsp;
              {data?.negative?.reviews?.pageInfo?.total}
            </Tab>
          </TabBar>
          <UserReviewsFeedContainer
            className={s.feed}
            username={username}
            rating={params.r ? ratingParamToReviewRating[params.r] : undefined}
            category={params.t ? categoryParamToReviewCategory[params.t] : undefined}
          />
        </div>
      </div>
    )
  );
};

export default ProfileReviewsPage;
